import classNames from 'classnames';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import type { FC, JSX } from 'react';
import { memo } from 'react';
import {
  ConversationMessageToolConsentEnum,
  type Message,
} from 'types/ai.types';
import AIMessageAvatar from './AIMessageAvatar';
import AIMessageButtonTalkToCoach from './AIMessageButtonTalkToCoach';
import AIMessageMetadataFile from './AIMessageMetadataFile';
import AIMessageParts from './AIMessageParts';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { SubscriptionPlanNameEnum } from 'types/users.types';

type AIMessageItemProps = {
  message: Message;
  isLastAssistantMessage?: boolean;
  isSmall?: boolean;
  isFullWidth?: boolean;
  renderMessage?: (message: Message) => JSX.Element;
  className?: string;
};

const AIMessageItem: FC<AIMessageItemProps> = memo(function AIMessage({
  message,
  isFullWidth = true,
  className,
  isSmall,
  isLastAssistantMessage,
  renderMessage,
}) {
  const user = useGetCurrentCustomerData().data;
  const { metadataItemsMap, isStreaming, isLoading } = useAIChatContext();
  const messageMetadataFiles = (message.metadataIds ?? [])
    .map((id) => metadataItemsMap[id])
    .filter((m) => m?.documentType === 'pdf' || m?.documentType === 'image');

  const lastPart = message.parts[message.parts.length - 1];

  const hasAccessToTaxCoach =
    user &&
    user.subscriptionPlan.planInternalName !== SubscriptionPlanNameEnum.free &&
    !(
      [
        SubscriptionPlanNameEnum.e_invoicing1,
        SubscriptionPlanNameEnum.e_invoicing12,
      ].includes(user.subscriptionPlan.planInternalName) &&
      user.VATType == 'subjectToVAT'
    );

  return (
    <div
      className={classNames(
        'text-left flex flex-col mt-3 py-3 rounded-10 text-primary items-start',
        isFullWidth ? 'w-full' : 'w-[90%]',
        message.role === 'user' ? 'bg-primary-50 !w-3/4' : 'bg-transparent',
        message.role === 'user' ? 'self-end' : 'self-start',
        !isSmall || message.role === 'user' ? 'px-3' : '',
        className,
      )}
    >
      {messageMetadataFiles.map((metadata, i) => (
        <AIMessageMetadataFile key={metadata._id ?? i} metadata={metadata} />
      ))}

      <div className="flex flex-row items-start gap-2 w-full">
        <AIMessageAvatar
          role={message.role}
          isSmall={!!isSmall}
          isStreaming={isStreaming || isLoading}
          isLastAssistantMessage={isLastAssistantMessage}
        />
        {renderMessage ? (
          renderMessage(message)
        ) : (
          <AIMessageParts
            messageParts={message.parts}
            messageId={message._id}
            isSmall={!!isSmall}
          />
        )}
      </div>

      {isLastAssistantMessage &&
        hasAccessToTaxCoach &&
        !isLoading &&
        !isStreaming &&
        !(
          lastPart?.type === 'tool' &&
          lastPart.consent == ConversationMessageToolConsentEnum.required
        ) && <AIMessageButtonTalkToCoach messageId={message._id} />}
    </div>
  );
});

export default AIMessageItem;
